import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Clock, Lock, Mail, AlertTriangle, X } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorDetail, setErrorDetail] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorDetail(null);

    try {
      const user = await login(email, password);
      toast.success(`Welcome, ${user.name}!`);
      if (user.role === "admin") {
        navigate("/");
      } else {
        navigate("/time-clock");
      }
    } catch (error) {
      console.error("Login error full object:", error);

      // Build a detailed diagnostic message
      let userMsg = "";
      let debugInfo = "";

      if (error.response) {
        // Server responded with an error
        const status = error.response.status;
        const detail = error.response.data?.detail;
        const dataRaw = typeof error.response.data === "string"
          ? error.response.data.substring(0, 200)
          : JSON.stringify(error.response.data).substring(0, 200);

        if (detail && typeof detail === "string") {
          userMsg = detail;
        } else {
          userMsg = `Server error (${status})`;
        }
        debugInfo = `Status: ${status} | Response: ${dataRaw}`;
      } else if (error.request) {
        // Request was sent but no response received
        userMsg = "No response from server — connection may be blocked";
        debugInfo = `Code: ${error.code || "unknown"} | Message: ${error.message}`;
      } else {
        // Error setting up the request
        userMsg = error.message || "Unknown error";
        debugInfo = `Type: setup | ${error.message}`;
      }

      toast.error(userMsg);
      setErrorDetail({ userMsg, debugInfo, timestamp: new Date().toISOString() });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-teal-50 flex items-center justify-center p-4" data-testid="login-page">
      <Card className="w-full max-w-md shadow-lg border-slate-200">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
            <Clock className="w-8 h-8 text-indigo-600" />
          </div>
          <CardTitle className="text-2xl font-bold text-slate-900">ESL Talks</CardTitle>
          <p className="text-slate-500 mt-1">Sign in to your account</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="pl-10"
                  required
                  data-testid="login-email"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="pl-10"
                  required
                  data-testid="login-password"
                />
              </div>
            </div>
            <Button
              type="submit"
              className="w-full bg-indigo-600 hover:bg-indigo-700"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>

          {/* Detailed error diagnostic box */}
          {errorDetail && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg relative" data-testid="login-error-detail">
              <button
                onClick={() => setErrorDetail(null)}
                className="absolute top-2 right-2 text-red-400 hover:text-red-600"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="font-semibold text-red-700 text-sm">{errorDetail.userMsg}</p>
                  <p className="text-red-500 text-xs mt-1 break-all font-mono">{errorDetail.debugInfo}</p>
                  <p className="text-red-400 text-[10px] mt-1">{errorDetail.timestamp}</p>
                </div>
              </div>
            </div>
          )}

          <div className="mt-4 text-center">
            <Link to="/forgot-password" className="text-sm text-indigo-600 hover:underline" data-testid="forgot-password-link">
              Forgot your password?
            </Link>
          </div>
          <div className="mt-4 pt-4 border-t border-slate-100 text-center">
            <p className="text-sm text-slate-500">
              New tutor?{" "}
              <Link to="/signup" className="text-teal-600 hover:underline font-medium" data-testid="signup-link">
                Create an Account
              </Link>
            </p>
          </div>
          <div className="mt-3 text-center">
            <p className="text-sm text-slate-500">
              Are you a student?{" "}
              <Link to="/student-login" className="text-emerald-600 hover:underline font-medium" data-testid="student-login-link">
                Student Portal
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
